import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getAccessLabel, isGated } from '../utils/accessLabels';
import { ImageIcon, Eye, User, Heart, Share2, Download, ChevronRight, Palette, ExternalLink } from 'lucide-react';
import AccessLevelBadge from './AccessLevelBadge';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import DownloadPaywall from './DownloadPaywall';
import ShareModal from './ShareModal';
import SignupInviteModal from './SignupInviteModal';

const LatestDeepWaveImages = ({ limit = 6, showTitle = true, className = "", columns = 3, excludeId = null, viewAllPosition = 'top' }) => {
    const gridColsClass = columns >= 4 ? 'lg:grid-cols-4' : 'lg:grid-cols-3';
    const [images, setImages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [downloadPaywall, setDownloadPaywall] = useState({ isOpen: false, image: null });
    const [shareModal, setShareModal] = useState({ isOpen: false, image: null });
    const [signupInvite, setSignupInvite] = useState({ isOpen: false, contentType: 'image' });
    const { isAuthenticated, hasActiveSubscription } = useAuth();

    useEffect(() => {
        fetchDeepWaveImages();
    }, [limit, excludeId]);

    const fetchDeepWaveImages = async () => {
        try {
            setLoading(true);
            setError(null);

            // Fetch images from The Deep Wave (user_id: 7)
            const response = await api.get(`/upload/public/image?limit=20`); // Fetch reasonable amount to filter

            if (response.data.success) {
                // Filter for only The Deep Wave's images (user_id: 7)
                const deepWaveImages = response.data.data.filter(image => image.user_id === 7 && image.id !== excludeId);
                
                // Show filtered Deep Wave images, limited to requested amount
                setImages(deepWaveImages.slice(0, limit));
            } else {
                setError('Failed to load Deep Wave images');
            }
        } catch (err) {
            console.error('Error fetching Deep Wave images:', err);
            setError('Unable to load images. Please try again later.');
        } finally {
            setLoading(false);
        }
    };

    const handleDownloadClick = (image) => {
        setDownloadPaywall({ isOpen: true, image });
    };

    const handleShareClick = (image) => {
        setShareModal({ isOpen: true, image });
    };

    const handleLike = async (image) => {
        if (!isAuthenticated) {
            setSignupInvite({ isOpen: true, contentType: 'image' });
            return;
        }

        try {
            const response = await api.post(`/upload/${image.id}/like`);
            
            if (response.data.success) {
                setImages(prev => prev.map(img => 
                    img.id === image.id 
                        ? { ...img, like_count: response.data.data.likeCount, user_has_liked: response.data.data.liked }
                        : img
                ));
            }
        } catch (err) {
            console.error('Error toggling like:', err);
        }
    };

    const formatUploadDate = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffTime = Math.abs(now - date);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        
        if (diffDays === 1) return 'Yesterday';
        if (diffDays < 7) return `${diffDays} days ago`;
        if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
        return `${Math.floor(diffDays / 30)} months ago`;
    };

    const formatFileSize = (bytes) => {
        if (!bytes) return '';
        const mb = (bytes / (1024 * 1024)).toFixed(1);
        return `${mb} MB`;
    };

    if (loading) {
        return (
            <div className={`latest-deepwave-images ${className}`}>
                {showTitle && (
                    <div className="mb-6">
                        <div className="flex items-center gap-3">
                            <ImageIcon className="w-6 h-6 text-blue-400" />
                            <h2 className="text-lg sm:text-xl font-bold text-white">Latest Artwork by The Deep Wave</h2>
                        </div>
                    </div>
                )}
                
                <div className={`grid grid-cols-1 sm:grid-cols-2 ${gridColsClass} gap-3 sm:gap-4`}>
                    {[...Array(limit)].map((_, index) => (
                        <div key={index} className="bg-gray-800/30 rounded-lg border border-gray-700/50 animate-pulse">
                            <div className="aspect-square bg-gray-700"></div>
                            <div className="p-4">
                                <div className="h-4 bg-gray-700 rounded w-3/4 mb-2"></div>
                                <div className="h-3 bg-gray-700 rounded w-1/2"></div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className={`latest-deepwave-images ${className}`}>
                {showTitle && (
                    <div className="mb-6">
                        <div className="flex items-center gap-3">
                            <ImageIcon className="w-6 h-6 text-blue-400" />
                            <h2 className="text-lg sm:text-xl font-bold text-white">Latest Artwork by The Deep Wave</h2>
                        </div>
                    </div>
                )}
                
                <div className="text-center py-8">
                    <div className="text-4xl mb-2">🎨</div>
                    <p className="text-gray-400">{error}</p>
                </div>
            </div>
        );
    }

    if (images.length === 0) {
        return (
            <div className={`latest-deepwave-images ${className}`}>
                {showTitle && (
                    <div className="mb-6">
                        <div className="flex items-center gap-3">
                            <ImageIcon className="w-6 h-6 text-blue-400" />
                            <h2 className="text-lg sm:text-xl font-bold text-white">Latest Artwork by The Deep Wave</h2>
                        </div>
                    </div>
                )}
                
                <div className="text-center py-8">
                    <div className="text-4xl mb-2">🖼️</div>
                    <p className="text-gray-400 mb-4">No official Deep Wave artwork yet</p>
                    <p className="text-sm text-gray-500">Check back soon for new releases!</p>
                </div>
            </div>
        );
    }

    return (
        <div className={`latest-deepwave-images ${className}`}>
            {showTitle && (
                <div className="mb-6">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0">
                        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
                            <div className="flex items-center gap-3">
                                <ImageIcon className="w-6 h-6 text-blue-400" />
                                <h2 className="text-lg sm:text-xl font-bold text-white">Latest Artwork by The Deep Wave</h2>
                            </div>
                            <span className="text-xs bg-blue-500/20 text-blue-400 px-2 py-1 rounded-full self-start w-fit">
                                Official
                            </span>
                        </div>
                        {viewAllPosition === 'top' && (
                            <Link
                                to="/feed/artist/TheDeepWave/images"
                                className="text-xs sm:text-sm text-blue-400 hover:text-blue-300 flex items-center gap-1 transition-colors self-start sm:self-auto"
                            >
                                View All
                                <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4" />
                            </Link>
                        )}
                    </div>
                </div>
            )}

            <div className={`grid grid-cols-1 sm:grid-cols-2 ${gridColsClass} gap-3 sm:gap-4`}>
                {images.map((image, index) => (
                    <div 
                        key={image.id}
                        className="group bg-gray-800/30 rounded-lg border border-gray-700/50 hover:border-blue-500/30 transition-all duration-300 hover:scale-[1.02] overflow-hidden"
                    >
                        {/* Image Display */}
                        <div className="relative aspect-square bg-gray-900 overflow-hidden">
                            <img
                                src={image.optimized_url || image.file_url}
                                alt={image.title || 'Artwork'}
                                className="w-full h-full object-cover"
                                loading="lazy"
                            />

                            {isGated(image.access_level, isAuthenticated, hasActiveSubscription) ? (
                                /* Protected: always-visible gradient overlay */
                                <Link
                                    to={`/view/image/${image.username}/${image.stored_filename || image.original_filename || image.id}`}
                                    className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent flex items-end justify-center pb-3"
                                    onClick={e => e.stopPropagation()}
                                >
                                    <span className="text-white text-xs font-medium bg-black/60 backdrop-blur-sm px-3 py-1 rounded-full">{getAccessLabel(image.access_level, 'view')}</span>
                                </Link>
                            ) : (
                                /* Public: hover overlay with view button */
                                <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                                    <div className="flex gap-2">
                                        <Link
                                            to={`/view/image/${image.username}/${image.stored_filename || image.original_filename || image.id}`}
                                            className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-r from-blue-600 to-blue-800 hover:from-blue-500 hover:to-blue-700 rounded-lg flex items-center justify-center transition-all duration-300 transform hover:scale-110"
                                            title="View Full Size"
                                        >
                                            <Eye className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                                        </Link>
                                    </div>
                                </div>
                            )}

                            {/* File Size Badge */}
                            <div className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
                                {formatFileSize(image.file_size)}
                            </div>


                            <AccessLevelBadge accessLevel={image.access_level} position="top-right" isAuthenticated={isAuthenticated} hasActiveSubscription={hasActiveSubscription} />
                        </div>

                        {/* Image Info */}
                        <div className="p-3 sm:p-4">
                            <div className="flex items-start justify-between mb-2 gap-2">
                                <h3 className="font-semibold text-white text-xs sm:text-sm group-hover:text-blue-300 transition-colors truncate max-w-[70%] sm:max-w-[80%]">
                                    {image.title || `Artwork ${index + 1}`}
                                </h3>
                            </div>

                            <div className="mb-3">
                                <div className="flex items-center justify-between text-xs text-gray-400">
                                    <div className="flex items-center gap-1 min-w-0 flex-1">
                                        <div className="w-3 h-3 bg-gradient-to-r from-blue-500 to-blue-700 rounded-full flex items-center justify-center flex-shrink-0">
                                            <span className="text-white text-xs">🌊</span>
                                        </div>
                                        <span className="hidden sm:inline flex-shrink-0">The Deep Wave</span>
                                        <span className="hidden sm:inline flex-shrink-0">•</span>
                                        <span className="truncate max-w-[50%] sm:max-w-none">{formatUploadDate(image.created_at)}</span>
                                    </div>
                                    <div className="flex items-center gap-1 flex-shrink-0">
                                        <Heart className="w-3 h-3" />
                                        <span className="whitespace-nowrap">{(image.like_count || 0).toLocaleString()}</span>
                                    </div>
                                </div>
                            </div>

                            {/* Description */}
                            {image.description && (
                                <p className="text-xs text-gray-400 line-clamp-2 mb-3 break-words">
                                    {image.description}
                                </p>
                            )}

                            {/* Action Buttons */}
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-0.5 flex-shrink-0">
                                    {isAuthenticated && (
                                        <>
                                            <button 
                                                onClick={() => handleLike(image)}
                                                className={`p-1 transition-colors ${image.user_has_liked ? 'text-red-400' : 'text-gray-400 hover:text-red-400'}`} 
                                                title={image.user_has_liked ? 'Liked' : 'Like'}
                                            >
                                                <Heart className={`w-3 h-3 ${image.user_has_liked ? 'fill-current' : ''}`} />
                                            </button>
                                            <button 
                                                onClick={() => handleShareClick(image)}
                                                className="p-1 text-gray-400 hover:text-blue-400 transition-colors" 
                                                title="Share"
                                            >
                                                <Share2 className="w-3 h-3" />
                                            </button>
                                        </>
                                    )}
                                    <Link
                                        to={`/view/image/${image.username}/${image.stored_filename || image.original_filename || image.id}`}
                                        className="p-1.5 text-gray-300 hover:text-blue-400 hover:bg-blue-500/10 rounded transition-all duration-300"
                                        title="View Image Page"
                                    >
                                        <ExternalLink className="w-4 h-4" />
                                    </Link>
                                    <button
                                        onClick={() => handleDownloadClick(image)}
                                        className="p-1 text-gray-400 hover:text-green-400 transition-colors"
                                        title="Download"
                                    >
                                        <Download className="w-3 h-3" />
                                    </button>
                                </div>
                                
                                <div className="flex items-center gap-1 text-xs text-gray-500 flex-shrink-0">
                                    <Palette className="w-3 h-3" />
                                    <span>Art</span>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {viewAllPosition === 'bottom' ? (
                <div className="mt-6 text-center">
                    <Link
                        to="/feed/artist/TheDeepWave/images"
                        className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-600/10 hover:bg-blue-600/20 text-blue-400 hover:text-blue-300 rounded-lg transition-all duration-200 hover:scale-105 text-sm border border-blue-600/20"
                    >
                        <ImageIcon className="w-3.5 h-3.5" />
                        View All Artwork
                    </Link>
                </div>
            ) : images.length >= limit && (
                <div className="mt-6 text-center">
                    <Link
                        to="/feed/artist/TheDeepWave/images"
                        className="inline-flex items-center gap-2 text-sm text-blue-400 hover:text-blue-300 transition-colors"
                    >
                        Load More Artwork
                        <ChevronRight className="w-4 h-4" />
                    </Link>
                </div>
            )}

            {/* Download Paywall Modal */}
            <DownloadPaywall
                isOpen={downloadPaywall.isOpen}
                onClose={() => setDownloadPaywall({ isOpen: false, image: null })}
                mediaTitle={downloadPaywall.image?.title || 'Unknown Artwork'}
                mediaType="image"
                uploadId={downloadPaywall.image?.id}
            />

            {/* Share Modal */}
            <ShareModal
                isOpen={shareModal.isOpen}
                onClose={() => setShareModal({ isOpen: false, image: null })}
                mediaTitle={shareModal.image?.title || 'Unknown Artwork'}
                mediaType="image"
                username={shareModal.image?.username || 'band'}
                mediaId={shareModal.image?.stored_filename || shareModal.image?.original_filename || shareModal.image?.id}
                artistName={shareModal.image?.display_name || shareModal.image?.username || 'band'}
            />

            {/* Signup Invite Modal */}
            <SignupInviteModal
                isOpen={signupInvite.isOpen}
                onClose={() => setSignupInvite({ isOpen: false, contentType: 'image' })}
                contentType={signupInvite.contentType}
            />
        </div>
    );
};

export default LatestDeepWaveImages;